import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Alert } from '@mui/material';
import {
    CloudUploadOutlined,
    InsertDriveFileOutlined,
    CloseOutlined,
    CheckCircleOutlined,
    ErrorOutline,
    UploadFileOutlined,
    FormatListBulletedOutlined
} from '@mui/icons-material';
import BreadcrumbsBar from '../../../layouts/breadcrumb/BreadcrumbsBar';
import Card from '../../../common/card/Card';
import Toast from '../../../common/toast/Toast';
import useToast from '../../../common/hooks/useToast';
import { uploadInstagramNamesExcel } from '../../../services/instagramNameFillerService';
import styles from './InstagramNameFiller.module.scss';

const InstagramNameFiller = () => {
    const { t } = useTranslation();
    const [file, setFile] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const { isToastOpen, toggleToast, toastContent, setToastContent } = useToast();

    const validateFile = (file) => {
        if (!file) return false;
        const validExtensions = ['.xlsx'];
        const fileName = file.name.toLowerCase();
        return validExtensions.some(ext => fileName.endsWith(ext));
    };

    const handleFile = useCallback((selectedFile) => {
        setError(null);

        if (!selectedFile) {
            return;
        }

        if (!validateFile(selectedFile)) {
            setError(t('instagram-name-filler.invalid-file-msg'));
            setFile(null);
            return;
        }

        setFile(selectedFile);
    }, [t]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        const droppedFile = e.dataTransfer.files[0];
        handleFile(droppedFile);
    }, [handleFile]);

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files[0];
        handleFile(selectedFile);
        // Reset input value to allow selecting the same file again
        e.target.value = '';
    };

    const handleRemoveFile = () => {
        setFile(null);
        setError(null);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleUpload = async () => {
        if (!file) return;

        setIsUploading(true);
        setError(null);

        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('file', file);

            const response = await uploadInstagramNamesExcel(formData);

            if (response && response.status === 200) {
                const fileBlob = response.data;
                const url = window.URL.createObjectURL(fileBlob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `IG_Names_Filled_${new Date().getTime()}.xlsx`;
                a.click();

                // Clean up
                window.URL.revokeObjectURL(url);

                setToastContent('File processed and downloaded successfully!');
                setFile(null);
            } else {
                setError('Upload failed. Please try again.');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError('Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            toggleToast();
        }
    };

    const getDropZoneClass = () => {
        let className = styles.dropZone;
        if (isDragActive) className += ` ${styles.dragActive}`;
        if (file) className += ` ${styles.hasFile}`;
        if (error) className += ` ${styles.hasError}`;
        return className;
    };

    return (
        <div className={styles.container}>
            <BreadcrumbsBar />
            <Card title={t('instagram-name-filler.title')} customIcon={<FormatListBulletedOutlined />}>
                <div style={{ marginBottom: '20px' }}>
                    <Alert severity="info">
                        <strong>{t('instagram-name-filler.excel-format-title')}:</strong> {t('instagram-name-filler.excel-format-desc')}
                    </Alert>
                </div>
                <div className={styles.dropZoneWrapper}>
                    <div
                        className={getDropZoneClass()}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={handleBrowseClick}
                    >
                        <div className={styles.iconContainer}>
                            <CloudUploadOutlined className={styles.uploadIcon} />
                        </div>


                        <div className={styles.dropZoneText}>
                            <div className={styles.mainText}>
                                {t('instagram-name-filler.drag-drop-msg')}
                            </div>
                            <div className={styles.orText}>
                                {t('instagram-name-filler.or-text')}
                            </div>
                            <Button
                                variant="contained"
                                className={styles.browseButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleBrowseClick();
                                }}
                            >
                                {t('instagram-name-filler.browse-btn')}
                            </Button>
                            <div className={styles.formatText}>
                                <CheckCircleOutlined className={styles.formatIcon} />
                                {t('instagram-name-filler.supported-format')}
                            </div>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx"
                            className={styles.hiddenInput}
                            onChange={handleFileInputChange}
                        />
                    </div>

                    {file && (
                        <div className={styles.fileInfo}>
                            <InsertDriveFileOutlined className={styles.fileIcon} />
                            <div className={styles.fileDetails}>
                                <div className={styles.fileName}>{file.name}</div>
                                <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                            </div>
                            <IconButton
                                className={styles.removeButton}
                                onClick={handleRemoveFile}
                                size="small"
                            >
                                <CloseOutlined />
                            </IconButton>
                        </div>
                    )}

                    {error && (
                        <div className={styles.errorMessage}>
                            <ErrorOutline className={styles.errorIcon} />
                            {error}
                        </div>
                    )}

                    <div className={styles.uploadActions}>
                        <Button
                            variant="contained"
                            className={styles.uploadButton}
                            disabled={!file || isUploading}
                            onClick={handleUpload}
                            startIcon={<UploadFileOutlined />}
                        >
                            {t('instagram-name-filler.upload-btn')}
                        </Button>
                    </div>
                </div>
            </Card>

            <Toast
                isOpen={isToastOpen}
                onClose={toggleToast}
                content={toastContent}
                severity="info"
            />
        </div>
    );
};

export default InstagramNameFiller;
